/* Хвост «Текста» (§7.4, §16) на общем OrderTail: шапка — название экрана, статус «Распознаём речь» · «Можно уйти с экрана…»,
   герой — рамка вокруг исходника (волна записи, первый кадр, снимок или лист), этапы и деньги — из заказа сервера.
   Итог — вид для чтения, «Скачать» (TXT) и «Ещё вариант» или «Скопировать», «Что дальше» по задаче, главное — «Открыть в чате». */
import type { ReactNode } from 'react';
import { uiText } from '../../../i18n';
import type { CircleFormState } from '../../studio/circle/CircleFieldInput';
import { OrderTail, type TailAction, type TailTexts } from '../../studio/circle/OrderTail';
import { leaveTail } from '../../studio/circle/tool-helpers';
import { openStudioTarget } from '../../studio/studio-navigation';
import { useVideoSourcePreview } from '../../video/use-video-source-preview';
import { openProjectsWith } from '../../script/Projects';
import { choiceLabels, clipText, promptStyleNotes, toneCards, type TextKeys, type TextKind } from './text-model';
import { scriptOf, textDocumentPlain, textTailFail, type TextDocument } from './text-result-model';
import { TextReading, type ReadingContext } from './TextReading';
import { flatPeaks, type SourceMeta } from './text-media';
import { useTextResult } from './use-text-result';
import { downloadText, useTextCopy } from './use-text-copy';
import { PROMPT_MAX } from '../../studio/prompt-limit';

type TailKind = Exclude<TextKind, 'pnew' | 'pfix'>;
const R: Record<TailKind, { runH: string; ready: string; done: string; sent: string; modH: string; notMade: string; techH: string; edit: string; file: string }> = {
  stt: { runH: 'Распознаём речь', ready: 'расшифровка будет готова', done: 'Расшифровка готова', sent: 'расшифровку', modH: 'Запись не прошла проверку', notMade: 'текст не создан', techH: 'Не получилось распознать речь', edit: 'Заменить файл', file: 'steos-transcript.txt' },
  yt: { runH: 'Получаем текст ролика', ready: 'текст будет готов', done: 'Текст готов', sent: 'текст', modH: 'Ролик не прошёл проверку', notMade: 'текст не создан', techH: 'Не получилось получить текст', edit: 'Заменить ссылку', file: 'steos-youtube.txt' },
  seo: { runH: 'Пишем описание товара', ready: 'описание будет готово', done: 'Описание готово', sent: 'описание', modH: 'Описание не прошло проверку', notMade: 'описание не создано', techH: 'Не получилось написать описание', edit: 'Изменить данные', file: 'steos-description.txt' },
  pphoto: { runH: 'Пишем промпт', ready: 'промпт будет готов', done: 'Промпт готов', sent: 'промпт', modH: 'Фото не прошло проверку', notMade: 'промпт не создан', techH: 'Не получилось создать промпт', edit: 'Заменить фото', file: 'steos-prompt.txt' },
  pvideo: { runH: 'Пишем промпт', ready: 'промпт будет готов', done: 'Промпт готов', sent: 'промпт', modH: 'Видео не прошло проверку', notMade: 'промпт не создан', techH: 'Не получилось создать промпт', edit: 'Заменить видео', file: 'steos-prompt.txt' },
};
function tailTexts(kind: TailKind): TailTexts {
  const r = R[kind];
  const extra = kind === 'seo' ? ' Каждый блок копируется отдельно.' : kind === 'pphoto' || kind === 'pvideo' ? ' Можно сразу создать по нему фото.' : '';
  return {
    runH: r.runH,
    runP: `Можно уйти с экрана: бот напишет, когда ${r.ready}.`,
    doneH: r.done,
    doneP: `Бот уже прислал ${r.sent} в чат, в отдельную тему.${extra}`,
    modH: r.modH,
    notMade: r.notMade,
    techH: r.techH,
  };
}
/** Лист текстовых и волна записи в рамке статуса (docArt / waveArt прототипа) */
function DocArt() {
  return (
    <span className="tt-doc">
      <b />
      {[94, 100, 86, 97, 72, 90, 60].map((width, index) => (
        <i key={index} style={{ width: `${width}%`, ['--d' as string]: `${(index * 0.16).toFixed(2)}s` }} />
      ))}
    </span>
  );
}
function WaveArt({ peaks }: { peaks: number[] }) {
  return (
    <span className="tt-wv">
      {peaks.slice(0, 34).map((peak, index) => (
        <i key={index} style={{ ['--h' as string]: Math.max(0.12, peak).toFixed(2), ['--d' as string]: `${((index % 9) * 0.11).toFixed(2)}s` }} />
      ))}
    </span>
  );
}
export function TextTail({
  state,
  kind,
  keys,
  meta,
  onRetry,
}: {
  state: CircleFormState;
  kind: TailKind;
  keys: TextKeys;
  meta: Record<string, SourceMeta>;
  onRetry(): void;
}) {
  const sent = state.intent?.body.inputs ?? {};
  const input = (key: string | undefined) => (key && typeof sent[key] === 'string' ? (sent[key] as string) : key ? state.inputs[key] || '' : '');
  const photoKey = keys.photos.find(key => input(key));
  const photo = useVideoSourcePreview(photoKey ? input(photoKey) : undefined, state.ownerId, 'photo', 20 * 1048576);
  const result = useTextResult(state.order, state.ownerId);
  const copier = useTextCopy();
  const document = result.document;
  const plain = document ? textDocumentPlain(document) : '';
  const sourceKey = [keys.audio, keys.video].find(key => key && input(key));
  const source = sourceKey ? meta[sourceKey] : undefined;

  let picture: ReactNode = <DocArt />;
  let ratio = 188 / 236;
  let art = true;
  if ((kind === 'stt' || kind === 'pvideo') && source?.kind === 'video' && source.poster) {
    picture = <img src={source.poster} alt="" />;
    ratio = source.ratio ?? 16 / 9;
    art = false;
  } else if (kind === 'stt') {
    picture = <WaveArt peaks={source?.peaks ?? flatPeaks} />;
    ratio = 236 / 148;
  } else if ((kind === 'pphoto' || kind === 'seo') && photo.url) {
    picture = <img src={photo.url} alt="" />;
    ratio = 3 / 4;
    art = false;
  }

  const inputFail = textTailFail(state.order);
  const texts = tailTexts(kind);
  const failEdit: TailAction | null = inputFail ? { label: inputFail.edit, icon: 'edit', onClick: () => void leaveTail(state) } : null;
  const languageValue = input(keys.language);
  const context: ReadingContext = {
    fileName: source?.name,
    durationMs: source?.durationMs,
    language: document?.language || (languageValue && languageValue !== 'auto' ? choiceLabels[languageValue] ?? languageValue : ''),
    market: choiceLabels[input(keys.market)] ?? '',
    tone: toneCards[input(keys.tone)]?.name ?? '',
    style: promptStyleNotes[input(keys.style)]?.[0] ?? '',
    lang: choiceLabels[input(keys.lang)] ?? '',
    ytKind: choiceLabels[input(keys.ytKind)] ?? '',
  };
  const download: TailAction = {
    label: 'Скачать',
    icon: 'download',
    disabled: !document,
    onClick: () => downloadText(plain, R[kind].file),
  };
  const second: TailAction =
    kind === 'stt' || kind === 'yt'
      ? {
          label: copier.done === 'all' ? 'Скопировано' : 'Скопировать',
          icon: copier.done === 'all' ? 'check' : 'copy',
          disabled: !document,
          onClick: () => copier.copy('all', plain, window.document.querySelector('#ttRd')),
        }
      : { label: 'Ещё вариант', icon: 'refresh', onClick: () => void leaveTail(state) };
  const edit: TailAction = { label: 'Изменить', icon: 'edit', onClick: () => void leaveTail(state) };
  const toPhoto: TailAction = {
    label: 'Создать фото по промпту',
    icon: 'image',
    disabled: !document,
    onClick: () => openStudioTarget({ screen: 'photo', options: { mode: 'generate', description: clipText(plain, PROMPT_MAX) } }),
  };
  const toVideo: TailAction = {
    label: 'Создать видео по промпту',
    icon: 'video',
    disabled: !document,
    onClick: () => openStudioTarget({ screen: 'video', options: { mode: 'text', description: clipText(plain, PROMPT_MAX) } }),
  };
  const next: TailAction[] =
    kind === 'seo'
      ? [
          edit,
          {
            label: 'Сделать видео товара',
            icon: 'video',
            disabled: !document,
            onClick: () => openStudioTarget({ screen: 'video', options: { mode: 'product', description: clipText(productLine(document), PROMPT_MAX) } }),
          },
        ]
      : kind === 'pphoto'
        ? [edit, toPhoto, toVideo]
        : kind === 'pvideo'
          ? [edit, toVideo, toPhoto]
          : [
              edit,
              {
                label: 'Сделать описание товара',
                icon: 'text',
                disabled: !document,
                onClick: () =>
                  openStudioTarget({ screen: 'text', options: { mode: 'product_description', text: Array.from(plain).slice(0, 1120).join('') } }),
              },
              {
                label: 'Озвучить',
                icon: 'wave',
                disabled: !document,
                // «Озвучка проектов»: собеседники — роли, текст — реплики
                onClick: () => document && openProjectsWith({ ...scriptOf(document), name: 'Расшифровка' }),
              },
            ];
  return (
    <OrderTail
      state={state}
      kind="text"
      className={`text-tail tt-ar${art ? ' text-tail-art' : ''}`}
      texts={inputFail ? { ...texts, modH: inputFail.head, techH: inputFail.head, modWhy: inputFail.why, techWhy: inputFail.why } : texts}
      hero={{ shape: 'frame', ratio, picture }}
      result={
        document ? (
          <>
            <TextReading document={document} context={context} prompt={kind === 'pphoto' || kind === 'pvideo'} />
            {copier.failed === 'all' && (
              <p className="hintp" role="status">
                {uiText('Текст выделен: скопируйте его вручную')}
              </p>
            )}
          </>
        ) : state.order?.result?.available ? (
          <p role={result.error ? 'alert' : 'status'} className="hintp">
            {uiText(result.error ? 'Не удалось открыть результат.' : 'Загружаем…')}{' '}
            {result.error && (
              <button type="button" className="lnk" onClick={result.retry}>
                {uiText('Повторить')}
              </button>
            )}
          </p>
        ) : null
      }
      acts={[download, second]}
      next={next}
      modAction={inputFail?.edit ?? R[kind].edit}
      foot={failEdit ? { mod: failEdit, tech: failEdit } : undefined}
      onRetry={onRetry}
    />
  );
}
function productLine(document: TextDocument | null) {
  if (!document) return '';
  return document.blocks
    .filter(block => block.label === 'Заголовок' || block.label === 'Описание')
    .map(block => block.text)
    .join('\n');
}
